import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import authService from "../services/authService";

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = localStorage.getItem("vetriaai_jobseeker_user");
        if (storedUser) {
          const parsedStored = JSON.parse(storedUser);

          // CRITICAL FIX: If user is HR or Admin, DO NOT verify against Job Seeker API
          // This prevents the Job Seeker AuthProvider (which wraps the whole app) from
          // failing validation and clearing the session on refresh.
          if (parsedStored.userType === 'hr' || parsedStored.userType === 'admin') {
            console.log("[JS Auth] HR/Admin session detected. Skipping Job Seeker validation.");
            setUser(parsedStored);
            setIsLoading(false);
            return;
          }

          // Verify with backend and update
          try {
            const freshUser = await authService.getCurrentUser();

            // Merge with existing session data to preserve token
            const updatedUser = {
              ...parsedStored,
              ...freshUser,
              // Explicitly ensure token is preserved if freshUser doesn't have it
              token: parsedStored.token
            };

            setUser(updatedUser);
            localStorage.setItem("vetriaai_jobseeker_user", JSON.stringify(updatedUser));
          } catch (e) {
            console.log("Could not refresh user session, using stored data", e);
            setUser(JSON.parse(storedUser));
          }
        }
      } catch (error) {
        console.error("Auth initialization failed", error);
        localStorage.removeItem("vetriaai_jobseeker_user");
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // State to track which user type is currently attempting login (for OTP step)
  const [pendingUserType, setPendingUserType] = useState(null);

  const login = async (email, password, showErrorToast = true) => {
    setIsLoading(true);
    setPendingUserType(null); // Reset
    try {
      console.log("[Auth] Attempting Job Seeker Login...");
      const data = await authService.login(email, password);
      // data contains { access_token, token_type, user }
      const signedInUser = {
        ...data.user,
        token: data.access_token,
        userType: "jobseeker"
      };

      setUser(signedInUser);
      localStorage.setItem("vetriaai_jobseeker_user", JSON.stringify(signedInUser));
      return true;
    } catch (err) {
      // 1. Check for Job Seeker OTP Trigger
      if (err.response && err.response.status === 403 && (err.response.data.detail === "Verification Required" || err.response.data.detail === "Inactive user")) {
        console.log("Login: JS Verification Required");
        setPendingUserType('jobseeker');
        throw err; // Propagate to Login.jsx
      }

      // 2. Fallback to HR Login
      console.log("[Auth] Job Seeker Login failed. Attempting HR Login...");
      try {
        const hrData = await authService.hrLogin(email, password);
        // HR Login Success (Direct - Unlikely with OTP enabled)
        const signedInUser = {
          ...hrData.user,
          token: hrData.access_token,
          userType: "hr"
        };
        setUser(signedInUser);
        localStorage.setItem("vetriaai_jobseeker_user", JSON.stringify(signedInUser));
        localStorage.setItem("vetriaai_hr_user", JSON.stringify(signedInUser)); // Sync for HR App
        return true;

      } catch (hrErr) {
        // 3. Check for HR OTP Trigger
        if (hrErr.response && hrErr.response.status === 403 && hrErr.response.data.detail === "Verification Required") {
          console.log("Login: HR Verification Required");
          setPendingUserType('hr');
          throw hrErr; // Propagate to Login.jsx (structure matches JS error)
        }

        console.error("HR Login failed", hrErr);
        // If both fail, throw default error
        // Use original error or generic "Invalid credentials"
      }

      // If we reach here, both attempts failed
      const errorMessage = "Invalid email or password";
      if (showErrorToast) {
        toast.error(errorMessage);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };


  const register = async (userData) => {
    setIsLoading(true);
    try {
      await authService.register(userData);
      // Registration successful. We do NOT auto-login as verification is required.
      // The calling component (RegisterPage) will handle the OTP/Verification flow.
      return true;
    } catch (err) {
      console.error("Registration failed", err);
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : "Registration failed");
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtpLogin = async (email, otp) => {
    setIsLoading(true);
    try {
      const type = pendingUserType || 'jobseeker'; // Default to JS if state lost (e.g. refresh? but refresh clears state)
      console.log(`[Auth] Verifying OTP for userType: ${type}`);

      let data;
      if (type === 'hr') {
        data = await authService.hrVerifyOtp(email, otp, 'login');
      } else {
        data = await authService.verifyOtp(email, otp, 'login');
      }

      // data contains { access_token, user, status: 'success' }
      if (data.status === 'success' && data.access_token) {
        const signedInUser = {
          ...data.user,
          token: data.access_token,
          userType: type
        };
        setUser(signedInUser);
        localStorage.setItem("vetriaai_jobseeker_user", JSON.stringify(signedInUser));

        if (type === 'hr') {
          localStorage.setItem("vetriaai_hr_user", JSON.stringify(signedInUser));
        }

        return true;
      }
      return false;
    } catch (err) {
      console.error("OTP Validation failed", err);
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : "OTP Validation failed");
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
      setPendingUserType(null); // Clear state
    }
  };

  const googleSignIn = async (selectedType) => {
    toast.error("Google Sign-In is currently disabled for maintenance.");
    return false;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    localStorage.removeItem("vetriaai_jobseeker_user");
    localStorage.removeItem("currentSessionId");
    window.location.href = '/login';
  };

  const updateUserProfile = (updates) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem("vetriaai_jobseeker_user", JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, verifyOtpLogin, googleSignIn, logout, isLoading, updateUserProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

